import { capturePage, NOT_LOADED } from './capture'

/**
 * Small pictures of a Custom View's versions, for Versions. The page draws its own picture (capture.ts, as for a
 * Word export) the first time an owner's browser shows a version that has none; the top of it, cut to the card's
 * shape and shrunk to a JPEG, is kept with the version.
 */

/** The picture's size in pixels: twice the card's, for sharp screens. */
export const THUMBNAIL_WIDTH = 400
export const THUMBNAIL_HEIGHT = 250
/** The server refuses larger ones. */
export const MAX_THUMBNAIL_LENGTH = 120_000

/** How long to wait for the version's page to be shown and ready to be pictured. */
const FIND_TIMEOUT_MS = 15_000
const FIND_INTERVAL_MS = 500

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** The element a version's page is shown in, once it's in the page and ready to be pictured. */
async function shownPage(versionId: string): Promise<Element | null> {
  const deadline = Date.now() + FIND_TIMEOUT_MS
  while (Date.now() < deadline) {
    const element = document.querySelector(
      `[data-custom-view-version="${CSS.escape(versionId)}"] [data-custom-view-capture]`
    )
    if (element) return element
    await pause(FIND_INTERVAL_MS)
  }
  return null
}

/** A PNG cut to the top of the page in the card's shape and shrunk to a JPEG data: URL. */
async function shrink(png: ArrayBuffer): Promise<string | null> {
  const image = await createImageBitmap(new Blob([png], { type: 'image/png' }))
  const canvas = document.createElement('canvas')
  canvas.width = THUMBNAIL_WIDTH
  canvas.height = THUMBNAIL_HEIGHT
  const context = canvas.getContext('2d')
  if (!context) return null
  // The page's full width, from its top: as much of it as fits the card's shape.
  const sourceHeight = Math.min(image.height, (image.width * THUMBNAIL_HEIGHT) / THUMBNAIL_WIDTH)
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)
  context.drawImage(
    image,
    0,
    0,
    image.width,
    sourceHeight,
    0,
    0,
    THUMBNAIL_WIDTH,
    (sourceHeight * THUMBNAIL_WIDTH) / image.width
  )
  image.close()
  for (const quality of [0.8, 0.6, 0.4]) {
    const url = canvas.toDataURL('image/jpeg', quality)
    if (url.length <= MAX_THUMBNAIL_LENGTH) return url
  }
  return null
}

/** A small picture of the version's page while it's shown, or null if it isn't shown or can't be pictured. */
export async function pictureShownVersion(versionId: string): Promise<string | null> {
  const element = await shownPage(versionId)
  if (!element) return null
  const width = element.getBoundingClientRect().width || THUMBNAIL_WIDTH
  // Drawn at the picture's own size: drawing a whole page larger only to shrink it is slow. A frame just shown
  // may not take requests for its picture yet, so it's asked again for a moment.
  const scale = Math.min(1, THUMBNAIL_WIDTH / width)
  const deadline = Date.now() + FIND_TIMEOUT_MS
  for (;;) {
    try {
      const capture = await capturePage(element, scale)
      return shrink(capture.data)
    } catch (error) {
      const notReady = error instanceof Error && error.message === NOT_LOADED
      if (!notReady || Date.now() >= deadline || !element.isConnected) throw error
      await pause(FIND_INTERVAL_MS)
    }
  }
}
